import React, { useEffect, useState, useContext } from "react";
import axios from 'axios';
import { CommonContext } from "contexts/CommonContext";

function padBase64(b64: string): string {
  const rem = b64.length % 4;
  return rem ? b64 + '='.repeat(4 - rem) : b64;
}

/** Base64URL -> Uint8Array */
export function b64uToBytes(b64u: string): Uint8Array {
  const b64 = padBase64(b64u.replace(/-/g, '+').replace(/_/g, '/'));
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Uint8Array/ArrayBuffer -> Base64URL */
export function bytesToB64u(buf: ArrayBuffer | Uint8Array): string {
  const u8 = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/** ArrayBuffer -> Base64URL */
export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  return bytesToB64u(buffer);
}

/** Base64URL -> ArrayBuffer */
export function base64UrlToArrayBuffer(b64u: string): ArrayBuffer {
  return b64uToBytes(b64u).buffer as ArrayBuffer;
}

// ─── GLOBAL CSS ───────────────────────────────────────────────────────────────
const G = `
@keyframes spc-spin    { to { transform:rotate(360deg); } }
@keyframes spc-slideup { from { transform:translateY(60px);opacity:0; } to { transform:translateY(0);opacity:1; } }
@keyframes spc-fadeup  { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
.spc-spinner { width:52px;height:52px;border:4px solid #d1fae5;border-top-color:#16a34a;border-radius:50%;animation:spc-spin .9s linear infinite; }
.spc-modal-overlay { position:fixed;inset:0;background:rgba(0,0,0,.5);display:flex;align-items:flex-end;justify-content:center;z-index:9999; }
.spc-modal-box { background:#fff;border-radius:20px 20px 0 0;max-width:480px;width:100%;max-height:82vh;overflow:hidden;display:flex;flex-direction:column;animation:spc-slideup .3s ease; }
@media(min-width:600px){ .spc-modal-overlay{align-items:center;padding:20px;} .spc-modal-box{border-radius:20px;} }
.spc-modal-scroll { padding:4px 22px 32px;overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch; }
.spc-modal-scroll h3 { font-family:'Titillium Web','Segoe UI',sans-serif;font-size:14px;font-weight:700;color:#0f172a;margin:20px 0 5px; }
.spc-modal-scroll h3:first-child { margin-top:0; }
.spc-modal-scroll p { font-family:'Roboto',sans-serif;font-size:13px;color:#64748b;line-height:1.7;margin:0 0 4px; }
`;

// ─── WARN BANNER ─────────────────────────────────────────────────────────────
const WarnBanner: React.FC<{ blue?: boolean }> = ({ blue }) => (
  <div style={{
    backgroundColor: blue ? '#eff6ff' : '#fef9c3',
    border: `1px solid ${blue ? '#93c5fd' : '#fcd34d'}`,
    borderRadius: '10px', padding: '11px 14px',
    fontSize: '12.5px', color: blue ? '#1d4ed8' : '#92400e',
    fontFamily: "'Roboto',sans-serif", lineHeight: '1.55', fontWeight: '500',
  }}>
    Disable biometric payments at any time from the Manage Biometrics section.
  </div>
);

// ─── PRIVACY MODAL ────────────────────────────────────────────────────────────
const PrivacyModal: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:9999 }}>
    <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:'20px 20px 0 0', maxWidth:'480px', width:'100%', maxHeight:'82vh', overflow:'hidden', display:'flex', flexDirection:'column' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'20px 22px 14px', borderBottom:'1px solid #f1f5f9', flexShrink:0 }}>
        <h2 style={{ fontSize:'18px', fontWeight:'700', color:'#0f172a', margin:0, fontFamily:"'Titillium Web',sans-serif" }}>Privacy Policy</h2>
        <button onClick={onClose} style={{ background:'none', border:'none', fontSize:'26px', lineHeight:'1', cursor:'pointer', color:'#94a3b8', padding:'2px 8px' }}>×</button>
      </div>
      <div className="spc-modal-scroll">
        {[
          ['Biometric Payment Data',  'Pine Labs collects and securely stores biometric authentication credentials (passkeys) to enable Secure Payment Confirmation (SPC).'],
          ['How We Use Your Data',    'Your biometric credential is used exclusively to authenticate payment transactions. Raw biometric data never leaves your device.'],
          ['Data Storage & Security', 'Credentials are stored using FIDO2/WebAuthn on Pine Labs secure servers, protected via TLS and AES-256 at rest.'],
          ['Sharing & Third Parties', 'We do not sell or share your credential, except as required by payment network rules to complete authentication.'],
          ['Your Rights & Control',   'You may disable biometric payments at any time from Manage Biometrics. Credentials deleted within 30 days.'],
          ['Retention',               'Credentials retained while biometric payments are enabled. Inactive credentials deleted after 12 months.'],
        ].map(([title, body], i) => (
          <div key={i} style={{ marginTop: i === 0 ? 0 : '16px' }}>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

type PageView    = 'form' | 'processing' | 'success' | 'redirecting' | 'already';
type ProcessStep = 'idle' | 'verifying' | 'storing' | 'finalizing' | 'done';

const SPC_REGISTRATION_TYPE = {
  APP_FLOW: 1,
  OTP_FLOW: 2,
} as const;

// ─── FEATURE SLIDES ───────────────────────────────────────────────────────────
const FEATURE_SLIDES = [
  {
    icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>),
    title: 'Approve in One Tap',
    text:  "Complete your transaction with a biometric confirmation. No redirects – just one gesture and you're done.",
  },
  {
    icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/></svg>),
    title: 'Your Keys, Your Device',
    text:  'Passkeys stay encrypted on your device. We never see your biometric data.',
  },
  {
    icon: (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>),
    title: 'Faster Than SMS',
    text:  'Skip the wait. Your biometric confirms the payment in seconds – no codes, no lost messages, no expired OTPs.',
  },
];


// ─── Responsive width hook ────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = React.useState(() => typeof window !== 'undefined' ? window.innerWidth : 375);
  React.useEffect(() => {
    const onResize = () => setW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return w;
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const S = {
  heading:      { fontSize:'26px', fontWeight:'700', color:'#0f172a', margin:'0 0 24px', lineHeight:'1.3', textAlign:'center' as const, fontFamily:"'Titillium Web','Segoe UI',sans-serif" },
  authCard:     { border:'1px solid #e2e8f0', borderRadius:'12px', padding:'16px', marginBottom:'14px', backgroundColor:'#fff', boxShadow:'0 1px 6px rgba(0,0,0,.05)' },
  shieldCircle: { width:'44px', height:'44px', borderRadius:'50%', backgroundColor:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  authTitle:    { fontSize:'15px', fontWeight:'600', color:'#0f172a', margin:'0 0 4px', fontFamily:"'Titillium Web',sans-serif" },
  authText:     { fontSize:'13px', color:'#64748b', margin:0, lineHeight:'1.6', fontFamily:"'Roboto',sans-serif" },
  primaryBtn:   { width:'100%', padding:'16px', fontSize:'16px', fontWeight:'700', color:'#fff', backgroundColor:'#064e3b', border:'none', borderRadius:'12px', cursor:'pointer', letterSpacing:'0.3px', fontFamily:"'Titillium Web','Segoe UI',sans-serif", display:'block' as const, textAlign:'center' as const },
  procTitle:    { fontSize:'24px', fontWeight:'700', color:'#0f172a', textAlign:'center' as const, margin:'0 0 8px', fontFamily:"'Titillium Web','Segoe UI',sans-serif" },
  procSub:      { fontSize:'13px', color:'#6b7280', textAlign:'center' as const, lineHeight:'1.7', margin:'0 0 20px', fontFamily:"'Roboto',sans-serif" },
  progBar:      { height:'4px', backgroundColor:'#e5e7eb', borderRadius:'2px', overflow:'hidden' as const },
  progFill:     { height:'100%', backgroundColor:'#16a34a', borderRadius:'2px', transition:'width .5s ease' },
  successTitle: { fontSize:'24px', fontWeight:'700', color:'#0f172a', textAlign:'center' as const, margin:'0 0 12px', fontFamily:"'Titillium Web',sans-serif", lineHeight:'1.3' },
  successSub:   { fontSize:'14px', color:'#6b7280', textAlign:'center' as const, lineHeight:'1.65', margin:'0 0 24px', fontFamily:"'Roboto',sans-serif" },
};

// ─── LAYOUT HELPERS ──────────────────────────────────────────────────────────
const pageStyle = (isMobile: boolean): React.CSSProperties => ({
  minHeight: '100vh', display: 'flex', justifyContent: 'center',
  alignItems: isMobile ? 'flex-start' : 'center',
  background: isMobile ? '#fff' : '#f1f5f9',
  padding: isMobile ? '0' : '40px 20px',
  boxSizing: 'border-box',
  fontFamily: "'Roboto','Segoe UI',system-ui,sans-serif",
});
const cardStyle = (isMobile: boolean): React.CSSProperties => ({
  background: '#fff',
  width: isMobile ? '100%' : '440px',
  minHeight: isMobile ? '100vh' : 'auto',
  borderRadius: isMobile ? '0' : '20px',
  boxShadow: isMobile ? 'none' : '0 4px 32px rgba(0,0,0,.10)',
  padding: isMobile ? '36px 24px 32px' : '44px 40px 40px',
  boxSizing: 'border-box',
});
const successPageStyle = (isMobile: boolean): React.CSSProperties => ({
  minHeight: '100vh', display: 'flex', justifyContent: 'center',
  alignItems: isMobile ? 'flex-start' : 'center',
  background: '#f1f5f9',
  padding: isMobile ? '0' : '40px 20px',
  boxSizing: 'border-box',
});
const successCardStyle = (isMobile: boolean): React.CSSProperties => ({
  background: '#f1f5f9',
  width: isMobile ? '100%' : '440px',
  minHeight: isMobile ? '100vh' : 'auto',
  borderRadius: isMobile ? '0' : '20px',
  padding: isMobile ? '40px 24px 32px' : '44px 40px 40px',
  boxSizing: 'border-box',
});

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const SPCRegistration: React.FC = () => {
  const isMobile = useWindowWidth() < 640;

  const [cardNumber, setCardNumber]               = useState('');
  const [loading, setLoading]                     = useState(false);
  const [error, setError]                         = useState('');
  const [inputFocused, setInputFocused]           = useState(false);
  const [consentChecked, setConsentChecked]       = useState(false);
  const [pageView, setPageView]                   = useState<PageView>('form');
  const [spcCardHolderDataId, setSpcCardHolderDataId] = useState('');
  const [credentialId, setCredentialId]           = useState('');
  const [deviceInfo, setDeviceInfo]               = useState('');
  const [processStep, setProcessStep]             = useState<ProcessStep>('idle');
  const [lastCard, setLastCard]                   = useState('');
  const [showPrivacyModal, setShowPrivacyModal]   = useState(false);
  const [cardNetwork, setCardNetwork]             = useState('');
  const [slideIndex, setSlideIndex]               = useState(0);
  const [fromOtpFlow]                             = useState(() => !!sessionStorage.getItem('sendResponseToEMVData'));
  const [unauthorized, setUnauthorized]           = useState(false);
  const [isCardLoading, setIsCardLoading]         = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);
  const [redirectError, setRedirectError]         = useState('');

  const { fetchUrl, issuer } = useContext(CommonContext);

  // ── doReturn must be defined BEFORE init useEffect (it's a dependency) ─────
  const doReturn = React.useCallback(() => {
    const raw = sessionStorage.getItem('sendResponseToEMVData');
    if (!raw) return;
    try {
      const d = JSON.parse(raw);
      sessionStorage.removeItem('sendResponseToEMVData');
      sessionStorage.removeItem('spcCardNumber');
      sessionStorage.setItem('spcRegistrationComplete', JSON.stringify({ emvFinalCallbackResponseDTO: d.emvFinalCallbackResponseDTO }));
      window.location.href = d.returnUrl || '/';
    } catch (e) { console.error('doReturn error:', e); }
  }, []);

  // ── Init: App flow (uuid) → call backend; OTP flow → sessionStorage; else unauthorized
  useEffect(() => {
    // ── GUARD: fetchUrl not ready yet — wait for next render ─────────────────
    // Without this guard the effect fires with fetchUrl=null/undefined, which
    // makes axios call with "null/api/..." URL → network error → setUnauthorized(true)
    // even before a valid call is made (root cause of "new tab → instant unauthorized").
    if (!fetchUrl) return;

    const uuid         = new URLSearchParams(window.location.search).get('uuid');
    const hasOtpSession = !!sessionStorage.getItem('sendResponseToEMVData');

    if (uuid && !hasOtpSession) {
      // ── App flow: call backend once to get card number ────────────────────
      // Backend marks the request as processed in Redis after the FIRST call.
      // Any subsequent call (page refresh) returns an error → unauthorized page.
      // The fetchUrl guard above ensures we only call the backend once (when
      // fetchUrl is properly set), preventing a spurious first call with null URL.
      setIsCardLoading(true);
      axios.get(`${fetchUrl}/api/acs/spc/registration/${uuid}`)
        .then(res => {
          const d = res.data;
          if (d && d.responseCode === 0 && d.cardNumber) {
            setCardNumber(d.cardNumber);
          } else {
            setUnauthorized(true);
          }
        })
        .catch(() => setUnauthorized(true))
        .finally(() => setIsCardLoading(false));

    } else if (hasOtpSession) {
      // ── OTP flow: card number from sessionStorage ─────────────────────────
      const spcCard = sessionStorage.getItem('spcCardNumber');

      // ── KEY FIX: remove spcCardNumber IMMEDIATELY after reading ───────────
      // This makes it a single-use token. On any subsequent page load
      // (refresh / cancel / back-navigation), spcCardNumber won't be present
      // → else branch fires → doReturn() redirects user back to original page.
      // Without this removal, spcCardNumber stays forever and the registration
      // form keeps reappearing on every refresh.
      if (spcCard) {
        sessionStorage.removeItem('spcCardNumber');
        setCardNumber(spcCard);
      } else {
        // No card number available → session expired or page was refreshed.
        // Redirect back to wherever the user came from (completes OTP payment).
        doReturn();
      }
    } else {
      // Neither flow — unauthorized
      setUnauthorized(true);
    }
  }, [fetchUrl, doReturn]);

  // ── UNAUTHORIZED → redirect to existing error page ──────────────────────────
  useEffect(() => {
    if (unauthorized) window.location.href = '/spc/error';
  }, [unauthorized]);

  useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown <= 0) { doReturn(); return; }
    const t = setTimeout(() => setRedirectCountdown(c => c! - 1), 1000);
    return () => clearTimeout(t);
  }, [redirectCountdown, doReturn]);

  useEffect(() => {
    if (pageView === 'success' && fromOtpFlow) setRedirectCountdown(5);
  }, [pageView, fromOtpFlow]);

  useEffect(() => {
    if (error) { const t = setTimeout(() => setError(''), 5000); return () => clearTimeout(t); }
  }, [error]);

  useEffect(() => {
    const t = setInterval(() => {
      setSlideIndex(i => (i + 1) % FEATURE_SLIDES.length);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const triggerErrorRedirect = (msg: string) => {
    setRedirectError(msg); setPageView('redirecting'); setRedirectCountdown(5);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!consentChecked) { setError('Please accept the consent to proceed.'); return; }
    setLoading(true); setError(''); setLastCard(cardNumber);
    setPageView('processing'); setProcessStep('verifying');
    try {
      const initiateResponse = await axios.post(
        `${fetchUrl}/api/acs/spc/register/initiate`,
        { cardNumber, browserInfo: navigator.userAgent, spcRegistrationType: fromOtpFlow ? SPC_REGISTRATION_TYPE.OTP_FLOW : SPC_REGISTRATION_TYPE.APP_FLOW },
        { headers: { 'content-type': 'application/json' } }
      );
      const data = initiateResponse.data;
      if (data !== null && data.responseCode == 0) {
        const { acsChallenge, spcCardHolderDataId: spcId, rpId, timeoutInMS, cardNetwork: cn } = data;
        setSpcCardHolderDataId(spcId);
        if (cn) setCardNetwork(cn);
        // Attempt 1: WITH payment extension (Chrome + Edge that supports SPC)
        // Attempt 2: WITHOUT extension (Edge/Windows Hello payment ext error fallback)
        // Passkeys are OS-level (Windows Hello / Touch ID) so the credential is
        // accessible from any browser on the same device regardless of which browser
        // created it — Edge register → Chrome/Edge payment both work.
        const publicKeyBase: PublicKeyCredentialCreationOptions = {
          challenge: base64UrlToArrayBuffer(acsChallenge),
          rp: { name: 'Pine Labs ACS', id: rpId },
          user: {
            id: new TextEncoder().encode(spcId),
            name: `Card •••• ${cardNumber.slice(-4)}`,
            displayName: `Card •••• ${cardNumber.slice(-4)}`,
          },
          pubKeyCredParams: [{ alg: -7, type: 'public-key' }, { alg: -257, type: 'public-key' }],
          authenticatorSelection: { userVerification: 'required', residentKey: 'required', authenticatorAttachment: 'platform' },
          timeout: timeoutInMS, attestation: 'none',
        };

        let credential: PublicKeyCredential;
        try {
          credential = (await navigator.credentials.create({
            publicKey: { ...publicKeyBase, extensions: { payment: { isPayment: true } } as any },
          })) as PublicKeyCredential;
        } catch (extErr: any) {
          const isPaymentExtError =
            (extErr?.message || '').toLowerCase().includes('payment') ||
            (extErr?.message || '').toLowerCase().includes('credential identifier') ||
            extErr?.name === 'SecurityError' ||
            extErr?.name === 'NotSupportedError';
          if (isPaymentExtError) {
            // Edge/Windows Hello can't handle payment extension — retry without it
            credential = (await navigator.credentials.create({ publicKey: publicKeyBase })) as PublicKeyCredential;
          } else {
            throw extErr; // NotAllowedError (user cancelled) etc.
          }
        }
        if (!credential) throw new Error('Failed to create credential');
        setProcessStep('storing');
        let payload: any;
        if ((credential as any).toJSON) {
          const c = (credential as any).toJSON();
          payload = { spcCardHolderDataId: spcId, clientDataJSON: c.response.clientDataJSON, attestationObject: c.response.attestationObject, cardNumber };
        } else {
          const att = credential.response as AuthenticatorAttestationResponse;
          payload = { spcCardHolderDataId: spcId, clientDataJSON: arrayBufferToBase64Url(att.clientDataJSON), attestationObject: arrayBufferToBase64Url(att.attestationObject), cardNumber };
        }
        setProcessStep('finalizing');
        const completeResponse = await axios.post(`${fetchUrl}/api/acs/spc/register/complete`, payload, { headers: { 'content-type': 'application/json' } });
        const respData = completeResponse.data;
        if (respData !== null && respData.responseCode == 0) {
          setCredentialId('••••••••' + bytesToB64u(credential.rawId).slice(-6).toUpperCase());
          setDeviceInfo(`This Device - Registered on ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`);
          setProcessStep('done'); setPageView('success');
        } else {
          const msg = respData.responseMessage || 'Registration failed. Please try again.';
          if (fromOtpFlow) triggerErrorRedirect(msg); else { setError(msg); setPageView('form'); }
        }
      } else {
        if (data.alreadyRegistered) {
          if (data.cardNetwork) setCardNetwork(data.cardNetwork);
          const cred = data.spcCredential ? ('••••••••' + data.spcCredential.slice(-6).toUpperCase()) : 'Already Enabled';
          const regDate = data.spcRegistrationDate
            ? new Date(data.spcRegistrationDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
            : new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
          setCredentialId(cred);
          setDeviceInfo(`This Device - Registered on ${regDate}`);
          setProcessStep('done'); setPageView('already');
        } else {
          const msg = data.responseMessage || 'Initiation failed. Please try again.';
          if (fromOtpFlow) triggerErrorRedirect(msg); else { setError(msg); setPageView('form'); }
        }
      }
    } catch (err: any) {
      let msg = '';
      if (err.name === 'NotAllowedError') msg = 'Biometric registration was cancelled.';
      else if (err.name === 'NotSupportedError') msg = 'WebAuthn is not supported on this device.';
      else msg = err.response?.data?.responseMessage || err.message || 'Registration failed.';
      if (fromOtpFlow) triggerErrorRedirect(msg); else { setError(msg); setPageView('form'); }
    } finally { setLoading(false); setSpcCardHolderDataId(''); }
  };

  const handleCancel = () => {
    if (fromOtpFlow) { triggerErrorRedirect('Biometric setup was cancelled. Your payment is not affected.'); return; }
    setCardNumber(''); setConsentChecked(false); setError('');
  };

  const handleDone = () => {
    if (fromOtpFlow) { doReturn(); return; }
    setCardNumber(''); setConsentChecked(false); setCredentialId(''); setDeviceInfo(''); setLastCard(''); setError(''); setPageView('form');
  };

  const maskedDisplay = (raw: string) => {
    if (!raw) return '';
    if (raw.length < 16) return raw.replace(/\d/g, '\u2022').replace(/(\u2022{4})(?=\u2022)/g, '$1 ');
    return `\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${raw.slice(12)}`;
  };
  const handleCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {}; // no-op — always readOnly
  const handleCardFocus  = () => { setInputFocused(true); };
  const handleCardBlur   = () => { setInputFocused(false); };

  const labelVisible = cardNumber.length > 0 || inputFocused;
  const isReady      = cardNumber.length === 16 && consentChecked && !loading;
  const netName      = cardNetwork;

  // ── PROCESSING VIEW ──────────────────────────────────────────────────────────
  if (pageView === 'processing') {
    const stepOrder: ProcessStep[] = ['idle', 'verifying', 'storing', 'finalizing', 'done'];
    const STEPS = [
      { key: 'verifying'  as ProcessStep, label: 'Verifying attestation' },
      { key: 'storing'    as ProcessStep, label: 'Storing credentials'   },
      { key: 'finalizing' as ProcessStep, label: 'Finalizing enrollment' },
    ];
    const curIdx    = stepOrder.indexOf(processStep);
    const getStatus = (k: ProcessStep) => { const i = stepOrder.indexOf(k); return i < curIdx ? 'done' : i === curIdx ? 'active' : 'pending'; };
    const pct       = processStep === 'verifying' ? '33%' : processStep === 'storing' ? '66%' : '95%';
    return (
      <>
        <style>{G}</style>
        <div style={pageStyle(isMobile)}>
          <div style={cardStyle(isMobile)}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:'24px' }}>
              <div className="spc-spinner" />
            </div>
            <h2 style={S.procTitle}>Processing...</h2>
            <p style={S.procSub}>We're securely registering your biometric authentication. This will only take a moment.</p>
            <div style={S.progBar}><div style={{ ...S.progFill, width: pct }} /></div>
            <div style={{ marginTop:'20px', display:'flex', flexDirection:'column', gap:'10px' }}>
              {STEPS.map(step => {
                const st = getStatus(step.key);
                return (
                  <div key={step.key} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 14px', borderRadius:'10px', backgroundColor: st==='pending'?'#fafafa':'#f0fdf4', border:`1px solid ${st==='pending'?'#e5e7eb':'#bbf7d0'}` }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'50%', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', boxSizing:'border-box', background: st==='done'?'#16a34a':'#fff', border:`2px solid ${st!=='pending'?'#16a34a':'#d1d5db'}` }}>
                      {st === 'done' && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5"><polyline points="20 6 9 17 4 12"/></svg>}
                    </div>
                    <div>
                      <div style={{ fontSize:'14px', fontWeight:'600', color: st==='pending'?'#9ca3af':'#0f172a', fontFamily:"'Roboto',sans-serif" }}>{step.label}</div>
                      <div style={{ fontSize:'12px', marginTop:'1px', color: st==='done'?'#16a34a':st==='active'?'#2563eb':'#9ca3af', fontFamily:"'Roboto',sans-serif" }}>
                        {st==='done'?'Completed':st==='active'?'In progress...':'Pending'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop:'24px' }}><WarnBanner /></div>
          </div>
        </div>
      </>
    );
  }

  // ── REDIRECTING VIEW ─────────────────────────────────────────────────────────
  if (pageView === 'redirecting') {
    const pct = redirectCountdown !== null ? `${(redirectCountdown / 5) * 100}%` : '100%';
    return (
      <>
        <style>{G}</style>
        <div style={pageStyle(isMobile)}>
          <div style={cardStyle(isMobile)}>
            <div style={{ display:'flex', justifyContent:'center', marginBottom:'20px' }}>
              <div style={{ width:'84px', height:'84px', borderRadius:'50%', backgroundColor:'#fef2f2', border:'2px solid #fecaca', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
            </div>
            <h2 style={{ ...S.successTitle, color:'#991b1b' }}>Setup Incomplete</h2>
            <p style={{ ...S.successSub, marginBottom:'10px' }}>{redirectError}</p>
            <p style={{ ...S.successSub, fontSize:'12px', color:'#94a3b8', margin:'0 0 20px' }}>
              Your payment transaction is <strong style={{ color:'#16a34a' }}>not affected</strong>.
            </p>
            <div style={{ backgroundColor:'#fef9c3', border:'1px solid #fcd34d', borderRadius:'10px', padding:'14px 16px', marginBottom:'20px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' }}>
                <span style={{ fontSize:'13px', fontWeight:'600', color:'#92400e', fontFamily:"'Roboto',sans-serif" }}>
                  Auto redirecting…
                </span>
                <span style={{ fontSize:'22px', fontWeight:'700', color:'#064e3b', fontFamily:"'Titillium Web',sans-serif" }}>
                  {redirectCountdown}s
                </span>
              </div>
              <div style={{ height:'5px', backgroundColor:'#fde68a', borderRadius:'3px', overflow:'hidden' }}>
                <div style={{ height:'100%', backgroundColor:'#064e3b', borderRadius:'3px', width: pct, transition:'width 1s linear' }} />
              </div>
            </div>
            <div style={{ fontSize:'12px', color:'#94a3b8', textAlign:'center', fontFamily:"'Roboto',sans-serif" }}>
              Please wait, you will be redirected automatically.
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── ALREADY REGISTERED VIEW ──────────────────────────────────────────────────
  if (pageView === 'already') {
    return (
      <>
        <style>{G}</style>
        <div style={successPageStyle(isMobile)}>
          <div style={successCardStyle(isMobile)}>

            {/* Info circle — blue tint, shield icon */}
            <div style={{ display:'flex', justifyContent:'center', marginBottom:'20px' }}>
              <div style={{ width:'96px', height:'96px', borderRadius:'50%', backgroundColor:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'3px solid #3b82f6', opacity:0.3 }} />
                <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation:'spc-fadeup .5s ease both' }}>
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
            </div>

            <h2 style={{ ...S.successTitle, color:'#1e3a5f' }}>
              Already Registered!
            </h2>

            <p style={{ ...S.successSub }}>
              This device is already set up for biometric payments on your{' '}
              <strong style={{ color:'#0f172a' }}>{netName || 'Card'}</strong>{lastCard ? (
                <> ending in <strong style={{ color:'#0f172a' }}>{lastCard.slice(-4)}</strong></>
              ) : ''}.
            </p>

            {/* Info banner */}
            <div style={{ backgroundColor:'#eff6ff', border:'1px solid #93c5fd', borderRadius:'10px', padding:'11px 14px', marginBottom:'18px', fontSize:'13px', color:'#1d4ed8', fontFamily:"'Roboto',sans-serif", lineHeight:'1.55' }}>
              ℹ️ No action needed — biometric authentication is active for this card.
            </div>

            {/* Credential / Device details */}
            <div style={{ backgroundColor:'#fff', borderRadius:'14px', padding:'14px 16px', border:'1px solid #e5e7eb', marginBottom:'18px', boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>

              {/* Credential ID row */}
              <div style={{ display:'flex', alignItems:'center', gap:'12px', paddingBottom:'12px' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'50%', backgroundColor:'#eff6ff', border:'1px solid #bfdbfe', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#2563eb"/>
                    <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'2px', fontFamily:"'Roboto',sans-serif" }}>Credential ID</div>
                  <div style={{ fontSize:'13px', color:'#111827', fontWeight:'600', fontFamily:"'SF Mono','Courier New',monospace" }}>{credentialId}</div>
                </div>
              </div>

              <div style={{ height:'1px', backgroundColor:'#f1f5f9', marginBottom:'12px' }} />

              {/* Device / Registration date row */}
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'50%', backgroundColor:'#f8fafc', border:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'2px', fontFamily:"'Roboto',sans-serif" }}>Device</div>
                  <div style={{ fontSize:'13px', color:'#111827', fontWeight:'500', fontFamily:"'Roboto',sans-serif" }}>{deviceInfo}</div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom:'20px' }}><WarnBanner blue /></div>
            <button onClick={handleDone} style={S.primaryBtn}>Done</button>

            {fromOtpFlow && redirectCountdown !== null && (
              <div style={{ marginTop:'14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                  <span style={{ fontSize:'12px', color:'#64748b', fontFamily:"'Roboto',sans-serif" }}>Auto redirecting…</span>
                  <span style={{ fontSize:'16px', fontWeight:'700', color:'#064e3b', fontFamily:"'Titillium Web',sans-serif" }}>{redirectCountdown}s</span>
                </div>
                <div style={{ height:'4px', backgroundColor:'#e5e7eb', borderRadius:'2px', overflow:'hidden' }}>
                  <div style={{ height:'100%', backgroundColor:'#2563eb', borderRadius:'2px', width:`${(redirectCountdown / 5) * 100}%`, transition:'width 1s linear' }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── SUCCESS VIEW ─────────────────────────────────────────────────────────────
  if (pageView === 'success') {
    return (
      <>
        <style>{G}</style>
        <div style={successPageStyle(isMobile)}>
          <div style={successCardStyle(isMobile)}>

            {/* Check circle */}
            <div style={{ display:'flex', justifyContent:'center', marginBottom:'24px' }}>
              <div style={{ width:'96px', height:'96px', borderRadius:'50%', backgroundColor:'#e8f5f0', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                <div style={{ position:'absolute', inset:0, borderRadius:'50%', border:'3px solid #16a34a', opacity:0.3 }} />
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ animation:'spc-fadeup .5s ease both' }}>
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
            </div>

            <h2 style={S.successTitle}>Biometric Payments<br/>Enabled!</h2>

            <p style={S.successSub}>
              You can now use biometrics on this device to authenticate payments for your{' '}
              <strong style={{ color:'#0f172a' }}>{netName}</strong> card ending in{' '}
              <strong style={{ color:'#0f172a' }}>{lastCard.slice(-4)}</strong>.
            </p>

            {/* Credential / Device card */}
            <div style={{ backgroundColor:'#fff', borderRadius:'14px', padding:'14px 16px', border:'1px solid #e5e7eb', marginBottom:'18px', boxShadow:'0 1px 4px rgba(0,0,0,.05)' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', paddingBottom:'12px' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'50%', backgroundColor:'#f0fdf4', border:'1px solid #bbf7d0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" fill="#16a34a"/>
                    <polyline points="20 6 9 17 4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'2px', fontFamily:"'Roboto',sans-serif" }}>Credential ID</div>
                  <div style={{ fontSize:'13px', color:'#111827', fontWeight:'600', fontFamily:"'SF Mono','Courier New',monospace" }}>{credentialId}</div>
                </div>
              </div>
              <div style={{ height:'1px', backgroundColor:'#f1f5f9', marginBottom:'12px' }} />
              <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'50%', backgroundColor:'#f8fafc', border:'1px solid #e2e8f0', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18" strokeWidth="2.5"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize:'11px', fontWeight:'700', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'2px', fontFamily:"'Roboto',sans-serif" }}>Device</div>
                  <div style={{ fontSize:'13px', color:'#111827', fontWeight:'500', fontFamily:"'Roboto',sans-serif" }}>{deviceInfo}</div>
                </div>
              </div>
            </div>

            <div style={{ marginBottom:'20px' }}><WarnBanner blue /></div>
            <button onClick={handleDone} style={S.primaryBtn}>Done</button>

            {fromOtpFlow && redirectCountdown !== null && (
              <div style={{ marginTop:'14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                  <span style={{ fontSize:'12px', color:'#64748b', fontFamily:"'Roboto',sans-serif" }}>Auto redirecting…</span>
                  <span style={{ fontSize:'16px', fontWeight:'700', color:'#064e3b', fontFamily:"'Titillium Web',sans-serif" }}>{redirectCountdown}s</span>
                </div>
                <div style={{ height:'4px', backgroundColor:'#e5e7eb', borderRadius:'2px', overflow:'hidden' }}>
                  <div style={{ height:'100%', backgroundColor:'#16a34a', borderRadius:'2px', width:`${(redirectCountdown / 5) * 100}%`, transition:'width 1s linear' }} />
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  // ── FORM VIEW ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{G}</style>
      <div style={pageStyle(isMobile)}>
        <div style={cardStyle(isMobile)}>
          <h1 style={S.heading}>Enable Biometric<br />Payments</h1>

          <form onSubmit={handleSubmit} noValidate>

            {/* Card Number Input */}
            <div style={{ marginBottom:'16px' }}>
              <div style={{
                display:'flex', alignItems:'center', gap:'12px',
                border:`1.5px solid ${inputFocused ? '#0d9488' : '#e2e8f0'}`,
                borderRadius:'12px', padding:'10px 14px',
                backgroundColor:'#f9fafb',
                boxShadow: inputFocused ? '0 0 0 3px rgba(13,148,136,.10)' : 'none',
                transition:'border-color .2s, box-shadow .2s',
                boxSizing:'border-box',
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" style={{ flexShrink:0 }}
                  stroke={inputFocused ? '#0d9488' : '#94a3b8'} strokeWidth="2">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                  <line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                <div style={{ flex:1, display:'flex', flexDirection:'column', minWidth:0 }}>
                  <span style={{
                    display:'block', fontSize:'11px', fontWeight:'600', letterSpacing:'0.3px',
                    lineHeight:'14px', height:'14px', marginBottom:'3px',
                    color: inputFocused ? '#0d9488' : '#64748b',
                    fontFamily:"'Roboto',sans-serif",
                    visibility: labelVisible ? 'visible' : 'hidden',
                    transition:'color .2s',
                  }}>Card number</span>
                  <input
                    type="text"
                    value={maskedDisplay(cardNumber)}
                    readOnly
                    onFocus={handleCardFocus}
                    onBlur={handleCardBlur}
                    maxLength={19}
                    style={{
                      border:'none', outline:'none', padding:0, margin:0,
                      fontSize:'15px', fontWeight:'500', lineHeight:'1.2',
                      color: cardNumber.length > 0 ? '#0f172a' : '#94a3b8',
                      fontFamily:"'SF Mono','Courier New',monospace",
                      letterSpacing:'1.5px', backgroundColor:'transparent', width:'100%',
                      cursor:'default',
                    }}
                    aria-label="Card number"
                  />
                </div>
              </div>
            </div>

            {/* Feature Carousel */}
            <div style={{ ...S.authCard, overflow:'hidden', position:'relative', minHeight:'116px' }}>
              <div style={{ display:'flex', gap:'12px', alignItems:'flex-start', minHeight:'72px' }}>
                <div style={{ ...S.shieldCircle, flexShrink:0 }}>
                  {FEATURE_SLIDES[slideIndex].icon}
                </div>
                <div style={{ flex:1 }}>
                  <h3 key={`t${slideIndex}`} style={{ ...S.authTitle, animation:'spc-fadeup .35s ease both' }}>
                    {FEATURE_SLIDES[slideIndex].title}
                  </h3>
                  <p key={`p${slideIndex}`} style={{ ...S.authText, animation:'spc-fadeup .35s ease both' }}>
                    {FEATURE_SLIDES[slideIndex].text}
                  </p>
                </div>
              </div>
              <div style={{ display:'flex', gap:'5px', justifyContent:'center', marginTop:'10px' }}>
                {FEATURE_SLIDES.map((_, i) => (
                  <span key={i}
                    onClick={() => setSlideIndex(i)}
                    style={{ width: i===slideIndex?'18px':'5px', height:'5px', borderRadius:'3px', backgroundColor: i===slideIndex?'#1e293b':'#cbd5e1', display:'inline-block', cursor:'pointer', transition:'width .35s ease, background-color .35s ease' }}
                  />
                ))}
              </div>
            </div>

            {/* Warning banner */}
            <div style={{ marginBottom:'14px' }}><WarnBanner /></div>

            {/* Consent */}
            <label style={{ display:'flex', gap:'10px', alignItems:'flex-start', cursor:'pointer', marginBottom:'18px' }}>
              <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)}
                style={{ marginTop:'3px', accentColor:'#064e3b', cursor:'pointer', width:'15px', height:'15px', flexShrink:0 }} />
              <span style={{ fontSize:'13px', color:'#374151', lineHeight:'1.6', fontFamily:"'Roboto',sans-serif" }}>
                I consent to the storage of authentication credentials in accordance with the{' '}
                <button type="button" onClick={() => setShowPrivacyModal(true)}
                  style={{ color:'#2563eb', textDecoration:'underline', cursor:'pointer', background:'none', border:'none', padding:0, fontSize:'13px', fontFamily:"'Roboto',sans-serif", fontWeight:'normal', display:'inline' }}>
                  Privacy Policy
                </button>
              </span>
            </label>

            {/* Error */}
            {error && (
              <div style={{ backgroundColor:'#fef2f2', border:'1px solid #fecaca', borderRadius:'8px', padding:'10px 14px', fontSize:'13px', color:'#991b1b', marginBottom:'14px', display:'flex', alignItems:'center', gap:'8px', fontFamily:"'Roboto',sans-serif" }}>
                <span>⚠️</span><span style={{ flex:1 }}>{error}</span>
                <button type="button" onClick={() => setError('')} style={{ background:'none', border:'none', cursor:'pointer', fontSize:'18px', lineHeight:'1', color:'inherit', padding:0 }}>×</button>
              </div>
            )}

            {/* Submit */}
            <button type="submit" disabled={!isReady} style={{
              display:'block', width:'100%', marginBottom:'10px',
              padding:'15px 20px', fontSize:'16px', fontWeight:'700',
              color:'#fff', border:'none', borderRadius:'12px',
              cursor: isReady ? 'pointer' : 'not-allowed',
              backgroundColor: isReady ? '#064e3b' : '#94a3b8',
              opacity: isReady ? 1 : 0.65,
              letterSpacing:'0.3px', textAlign:'center',
              fontFamily:"'Titillium Web','Segoe UI',sans-serif",
              transition:'background-color .2s, opacity .2s',
            }}>
              Enable Biometric Payments
            </button>

            <button type="button" onClick={handleCancel} style={{
              display:'block', width:'100%', padding:'12px', fontSize:'14px', fontWeight:'500',
              color:'#6b7280', backgroundColor:'transparent', border:'none', borderRadius:'12px',
              cursor:'pointer', textAlign:'center', fontFamily:"'Roboto',sans-serif",
            }}>
              Cancel
            </button>
          </form>
        </div>
      </div>

      {showPrivacyModal && <PrivacyModal onClose={() => setShowPrivacyModal(false)} />}
    </>
  );
};


export default SPCRegistration;
